/**
 * ReachrView — CRM for outreach prospects & sequences
 *
 * Connects to /api/reachr/* endpoints (reachr-routes.ts).
 * Three sections: Stats → Prospects table → Sequences panel
 */

import { useState, useEffect, useCallback } from 'react';

// ── Types ─────────────────────────────────────────────────────────────────────

interface Prospect {
  _key?: string;
  id?: string;
  firstName: string;
  lastName: string;
  email: string;
  company: string;
  title?: string;
  status: 'new' | 'queued' | 'active' | 'paused' | 'completed' | 'unsubscribed' | 'bounced';
  source?: string;
  tags?: string[];
  outreachCount: number;
  responseCount: number;
  engagementScore: number;
  createdAt?: string;
}

interface Sequence {
  _key?: string;
  id?: string;
  name: string;
  description?: string;
  status: 'draft' | 'active' | 'paused' | 'completed';
  totalProspects: number;
  activeProspects: number;
  completedProspects: number;
  updatedAt?: string;
}

interface Stats {
  totalProspects: number;
  activeSequences: number;
  emailsSentToday: number;
  emailsSentThisWeek: number;
  openRate: number;
  replyRate: number;
  bounceRate: number;
}

// ── API helpers ───────────────────────────────────────────────────────────────

const BASE = '/api/reachr';

async function apiFetch<T>(path: string, opts?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    headers: { 'Content-Type': 'application/json' },
    ...opts,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error || `Request failed: ${res.status}`);
  }
  return res.json();
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function scoreColor(score: number): string {
  if (score >= 70) return 'amp-reachr-score--high';
  if (score >= 40) return 'amp-reachr-score--mid';
  return 'amp-reachr-score--low';
}

// ── StatusBadge ───────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: string }) {
  return (
    <span className="amp-reachr-badge" data-status={status}>
      {status}
    </span>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

export function ReachrView() {
  const [prospects, setProspects] = useState<Prospect[]>([]);
  const [sequences, setSequences] = useState<Sequence[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [showAddProspect, setShowAddProspect] = useState(false);
  const [prospectTotal, setProspectTotal] = useState(0);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 50;

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [statsData, seqData] = await Promise.all([
        apiFetch<Stats>('/stats'),
        apiFetch<{ data: Sequence[] }>('/sequences'),
      ]);
      setStats(statsData);
      setSequences(seqData.data || []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadProspects = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        page: String(page),
        pageSize: String(PAGE_SIZE),
        ...(search ? { search } : {}),
        ...(statusFilter !== 'all' ? { status: statusFilter } : {}),
      });
      const data = await apiFetch<{ data: Prospect[]; total: number }>(`/prospects?${params}`);
      setProspects(data.data || []);
      setProspectTotal(data.total || 0);
    } catch (err: any) {
      console.error('[reachr] prospects load failed:', err.message);
    }
  }, [page, search, statusFilter]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  useEffect(() => {
    loadProspects();
  }, [loadProspects]);

  if (error) {
    return (
      <div className="amp-reachr-error">
        <div className="amp-reachr-error-title">⚠ Reachr unavailable</div>
        <div className="amp-reachr-error-msg">{error}</div>
        <button className="amp-reachr-retry" onClick={loadAll}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="amp-reachr-wrap">
      {/* Header */}
      <div className="amp-header-bar">
        <div className="amp-chat-header-left">
          <span className="amp-brand-text">REACHR</span>
          <span className="amp-divider">|</span>
          <span className="amp-subtitle">Outreach CRM</span>
        </div>
        <button className="amp-btn-primary" onClick={() => setShowAddProspect(true)}>
          + Add Prospect
        </button>
      </div>

      {/* Stats bar */}
      {stats && <StatsBar stats={stats} />}

      {/* Main content — two panels */}
      <div className="amp-reachr-body">
        {/* Prospects panel */}
        <div className="amp-reachr-prospects-panel">
          {/* Prospect toolbar */}
          <div className="amp-reachr-toolbar">
            <input
              type="search"
              className="amp-reachr-search"
              placeholder="Search prospects…"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
            />
            <select
              className="amp-reachr-select"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
            >
              <option value="all">All statuses</option>
              <option value="new">New</option>
              <option value="queued">Queued</option>
              <option value="active">Active</option>
              <option value="completed">Completed</option>
              <option value="unsubscribed">Unsubscribed</option>
              <option value="bounced">Bounced</option>
            </select>
            <span className="amp-reachr-count">{prospectTotal} total</span>
          </div>

          {/* Prospect table */}
          <div className="amp-reachr-table-scroll">
            {loading ? (
              <LoadingRows />
            ) : prospects.length === 0 ? (
              <EmptyProspects onAdd={() => setShowAddProspect(true)} />
            ) : (
              <ProspectTable prospects={prospects} onRefresh={loadProspects} />
            )}
          </div>

          {/* Pagination */}
          {prospectTotal > PAGE_SIZE && (
            <div className="amp-reachr-pagination">
              <button
                className="amp-reachr-page-btn"
                disabled={page === 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                ← Prev
              </button>
              <span className="amp-reachr-page-info">
                Page {page} of {Math.ceil(prospectTotal / PAGE_SIZE)}
              </span>
              <button
                className="amp-reachr-page-btn"
                disabled={page * PAGE_SIZE >= prospectTotal}
                onClick={() => setPage((p) => p + 1)}
              >
                Next →
              </button>
            </div>
          )}
        </div>

        {/* Sequences panel */}
        <div className="amp-reachr-sequences-panel">
          <div className="amp-reachr-sequences-header">
            <span className="amp-brand-text" style={{ fontSize: '0.8rem' }}>
              Sequences
            </span>
            <CreateSequenceBtn onCreated={loadAll} />
          </div>
          <div className="amp-reachr-sequences-list">
            {sequences.length === 0 ? (
              <div className="amp-reachr-seq-empty">
                No sequences yet.
                <br />
                Create one to start automating outreach.
              </div>
            ) : (
              sequences.map((seq) => (
                <SequenceCard key={seq._key || seq.id} sequence={seq} onRefresh={loadAll} />
              ))
            )}
          </div>
        </div>
      </div>

      {/* Add Prospect Modal */}
      {showAddProspect && (
        <AddProspectModal
          onClose={() => setShowAddProspect(false)}
          onAdded={() => {
            setShowAddProspect(false);
            loadProspects();
            loadAll();
          }}
        />
      )}
    </div>
  );
}

// ── Stats Bar ─────────────────────────────────────────────────────────────────

function StatsBar({ stats }: { stats: Stats }) {
  const items = [
    { label: 'Prospects', value: stats.totalProspects, color: '#8b8bff' },
    { label: 'Active sequences', value: stats.activeSequences, color: '#6ee7b7' },
    { label: 'Sent today', value: stats.emailsSentToday, color: '#a5b4fc' },
    { label: 'Sent this week', value: stats.emailsSentThisWeek, color: '#a5b4fc' },
    { label: 'Open rate', value: `${(stats.openRate * 100).toFixed(1)}%`, color: '#fcd34d' },
    { label: 'Reply rate', value: `${(stats.replyRate * 100).toFixed(1)}%`, color: '#6ee7b7' },
    { label: 'Bounce rate', value: `${(stats.bounceRate * 100).toFixed(1)}%`, color: '#fca5a5' },
  ];

  return (
    <div className="amp-reachr-stats-bar">
      {items.map((item) => (
        <div key={item.label} className="amp-reachr-stat">
          <div className="amp-reachr-stat-label">{item.label}</div>
          <div className="amp-reachr-stat-value" style={{ color: item.color }}>
            {item.value}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Prospect Table ────────────────────────────────────────────────────────────

function ProspectTable({ prospects, onRefresh }: { prospects: Prospect[]; onRefresh: () => void }) {
  const handleDelete = async (id: string) => {
    if (!confirm('Delete this prospect?')) return;
    try {
      await apiFetch(`/prospects/${id}`, { method: 'DELETE' });
      onRefresh();
    } catch (err: any) {
      alert(err.message);
    }
  };

  return (
    <table className="amp-reachr-table">
      <thead>
        <tr>
          <th className="amp-reachr-th">Name</th>
          <th className="amp-reachr-th">Email</th>
          <th className="amp-reachr-th">Company</th>
          <th className="amp-reachr-th">Status</th>
          <th className="amp-reachr-th">Outreach</th>
          <th className="amp-reachr-th">Score</th>
          <th className="amp-reachr-th" style={{ width: 40 }}></th>
        </tr>
      </thead>
      <tbody>
        {prospects.map((p) => {
          const id = p._key || p.id || '';
          return (
            <tr key={id} className="amp-reachr-row">
              <td className="amp-reachr-td-name">
                {p.firstName} {p.lastName}
                {p.title && <div className="amp-reachr-td-name-title">{p.title}</div>}
              </td>
              <td className="amp-reachr-td-email">{p.email}</td>
              <td className="amp-reachr-td">{p.company}</td>
              <td className="amp-reachr-td">
                <StatusBadge status={p.status} />
              </td>
              <td className="amp-reachr-td-center">
                {p.outreachCount}
                {p.responseCount > 0 && (
                  <span className="amp-reachr-response">↩{p.responseCount}</span>
                )}
              </td>
              <td className={`amp-reachr-td-center ${scoreColor(p.engagementScore)}`}>
                {p.engagementScore}
              </td>
              <td style={{ padding: '8px 6px' }}>
                <button
                  className="amp-reachr-delete"
                  onClick={() => handleDelete(id)}
                  title="Delete"
                >
                  ✕
                </button>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

// ── Sequence Card ─────────────────────────────────────────────────────────────

function SequenceCard({ sequence, onRefresh }: { sequence: Sequence; onRefresh: () => void }) {
  const id = sequence._key || sequence.id || '';

  const toggle = async () => {
    try {
      const action = sequence.status === 'active' ? 'pause' : 'activate';
      await apiFetch(`/sequences/${id}/${action}`, { method: 'POST' });
      onRefresh();
    } catch (err: any) {
      alert(err.message);
    }
  };

  return (
    <div className="amp-reachr-seq-card">
      <div className="amp-reachr-seq-header">
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="amp-reachr-seq-name">{sequence.name}</div>
          {sequence.description && (
            <div className="amp-reachr-seq-desc">{sequence.description}</div>
          )}
        </div>
        <StatusBadge status={sequence.status} />
      </div>
      <div className="amp-reachr-seq-stats">
        <span>{sequence.totalProspects} total</span>
        <span className="amp-reachr-seq-active">{sequence.activeProspects} active</span>
        <span>{sequence.completedProspects} done</span>
      </div>
      {sequence.status !== 'draft' && sequence.status !== 'completed' && (
        <button className="amp-btn-secondary" onClick={toggle} style={{ marginTop: 8 }}>
          {sequence.status === 'active' ? 'Pause' : 'Activate'}
        </button>
      )}
    </div>
  );
}

// ── Create Sequence Button ────────────────────────────────────────────────────

function CreateSequenceBtn({ onCreated }: { onCreated: () => void }) {
  const [show, setShow] = useState(false);
  const [name, setName] = useState('');
  const [desc, setDesc] = useState('');
  const [saving, setSaving] = useState(false);

  const create = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      await apiFetch('/sequences', {
        method: 'POST',
        body: JSON.stringify({ name: name.trim(), description: desc.trim() }),
      });
      setName('');
      setDesc('');
      setShow(false);
      onCreated();
    } catch (err: any) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (!show) {
    return (
      <button className="amp-btn-secondary" onClick={() => setShow(true)}>
        + New
      </button>
    );
  }

  return (
    <div className="amp-reachr-create-popover">
      <div className="amp-reachr-create-title">New Sequence</div>
      <input
        autoFocus
        className="amp-input"
        placeholder="Sequence name"
        value={name}
        onChange={(e) => setName(e.target.value)}
      />
      <input
        className="amp-input"
        placeholder="Description (optional)"
        value={desc}
        onChange={(e) => setDesc(e.target.value)}
        style={{ marginTop: 8 }}
      />
      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <button
          className={`amp-reachr-submit${!name.trim() ? '' : ''}`}
          onClick={create}
          disabled={!name.trim() || saving}
          style={{
            flex: 1,
            background: name.trim() ? undefined : 'var(--border-subtle)',
            color: name.trim() ? undefined : 'var(--amp-text-dim)',
            padding: 8,
            borderRadius: 6,
            fontSize: '0.82rem',
          }}
        >
          {saving ? '…' : 'Create'}
        </button>
        <button
          className="amp-reachr-cancel"
          onClick={() => setShow(false)}
          style={{ padding: '8px 12px', borderRadius: 6, fontSize: '0.82rem' }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// ── Add Prospect Modal ────────────────────────────────────────────────────────

function AddProspectModal({ onClose, onAdded }: { onClose: () => void; onAdded: () => void }) {
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    title: '',
    source: 'manual',
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const update = (field: string, value: string) => setForm((prev) => ({ ...prev, [field]: value }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.email.trim() || !form.firstName.trim()) {
      setErr('First name and email are required');
      return;
    }
    setSaving(true);
    setErr('');
    try {
      await apiFetch('/prospects', { method: 'POST', body: JSON.stringify(form) });
      onAdded();
    } catch (error: any) {
      setErr(error.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="amp-modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="amp-modal">
        <div className="amp-modal-title">Add Prospect</div>
        <form className="amp-reachr-form" onSubmit={submit}>
          <div className="amp-reachr-form-row">
            <div className="amp-reachr-form-field">
              <label className="amp-label">First name *</label>
              <input
                autoFocus
                className="amp-input"
                value={form.firstName}
                onChange={(e) => update('firstName', e.target.value)}
                placeholder="John"
              />
            </div>
            <div className="amp-reachr-form-field">
              <label className="amp-label">Last name</label>
              <input
                className="amp-input"
                value={form.lastName}
                onChange={(e) => update('lastName', e.target.value)}
                placeholder="Smith"
              />
            </div>
          </div>
          <div>
            <label className="amp-label">Email *</label>
            <input
              type="email"
              className="amp-input"
              value={form.email}
              onChange={(e) => update('email', e.target.value)}
              placeholder="john@company.com"
            />
          </div>
          <div>
            <label className="amp-label">Company</label>
            <input
              className="amp-input"
              value={form.company}
              onChange={(e) => update('company', e.target.value)}
              placeholder="Acme Corp"
            />
          </div>
          <div>
            <label className="amp-label">Title</label>
            <input
              className="amp-input"
              value={form.title}
              onChange={(e) => update('title', e.target.value)}
              placeholder="Head of Engineering"
            />
          </div>
          {err && <div className="amp-reachr-form-error">{err}</div>}
          <div className="amp-reachr-form-actions">
            <button type="submit" className="amp-reachr-submit" disabled={saving}>
              {saving ? 'Adding…' : 'Add Prospect'}
            </button>
            <button type="button" className="amp-reachr-cancel" onClick={onClose}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function LoadingRows() {
  return (
    <div className="amp-reachr-loading">
      {[...Array(8)].map((_, i) => (
        <div key={i} className="amp-reachr-skeleton" style={{ opacity: 1 - i * 0.1 }} />
      ))}
    </div>
  );
}

function EmptyProspects({ onAdd }: { onAdd: () => void }) {
  return (
    <div className="amp-reachr-empty">
      <div className="amp-reachr-empty-icon">📋</div>
      <div className="amp-reachr-empty-title">No prospects yet</div>
      <div className="amp-reachr-empty-desc">
        Add prospects manually or import a CSV to start your outreach campaigns.
      </div>
      <button className="amp-btn-primary" onClick={onAdd} style={{ marginTop: 4 }}>
        Add first prospect
      </button>
    </div>
  );
}
